import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  TextField,
  Divider,
} from '@mui/material';

const TaskAnalysisFields = ({
  allGoalData,
  dataPoint,
  index,
  handleFieldChange,
}) => {
  const preventNumberScroll = (event) => {
    event.preventDefault();
    event.target.blur();
  };

  const taskAnalysisRaw =
    allGoalData?.TaskAnalysis || allGoalData?.dataCollection?.TaskAnalysis;

  const hasTaskAnalysis = taskAnalysisRaw?.HasTaskAnalysis === 'Yes';
  let taskAnalysisArray = taskAnalysisRaw?.TaskAnalysis || [];

  // FALLBACK: If no task analysis config but we have data, reconstruct it
  if (
    (!hasTaskAnalysis || taskAnalysisArray.length === 0) &&
    dataPoint.taskAnalysis &&
    Object.keys(dataPoint.taskAnalysis).length > 0
  ) {
    // Group the existing data by task analysis index
    const taskAnalysisGroups = {};

    Object.keys(dataPoint.taskAnalysis).forEach((key) => {
      const match = key.match(/^taskAnalysis(\d+)_step(\d+)$/);
      if (match) {
        const taskAnalysisIndex = parseInt(match[1]);
        const stepNumber = parseInt(match[2]);

        if (!taskAnalysisGroups[taskAnalysisIndex]) {
          taskAnalysisGroups[taskAnalysisIndex] = {};
        }

        taskAnalysisGroups[taskAnalysisIndex][stepNumber] = {
          StepNumber: stepNumber,
          Description: `Step ${stepNumber}`, // Placeholder description
          PromptingValues: dataPoint.taskAnalysis[key].promptingValues || {},
          // Determine prompting type based on the prompting values
          PromptingType: Object.keys(
            dataPoint.taskAnalysis[key].promptingValues || {}
          ).some(
            (k) =>
              k.includes('physical') ||
              k.includes('verbal-minimal') ||
              k.includes('verbal-moderate')
          )
            ? 'OT'
            : 'I, VP (IVP, DVP, PVP), GP, PP, ViP, M',
        };
      }
    });

    // Convert to the expected array format
    taskAnalysisArray = Object.keys(taskAnalysisGroups).map(
      (taskAnalysisIndex) => ({
        Steps: Object.values(taskAnalysisGroups[taskAnalysisIndex]).sort(
          (a, b) => a.StepNumber - b.StepNumber
        ),
      })
    );
  }

  // Define prompting options based on the original TaskAnalysis component
  const promptingOptions = {
    'I, VP (IVP, DVP, PVP), GP, PP, ViP, M': [
      { value: 'independent', label: 'Independent' },
      { value: 'physical', label: 'Physical' },
      { value: 'model', label: 'Model' },
      { value: 'visual', label: 'Visual' },
      { value: 'verbal', label: 'Verbal' },
      { value: 'gestural', label: 'Gestural' },
    ],
    OT: [
      {
        value: 'physical-full-total-assist',
        label: 'Physical → Full → Total Assist (75+% effort)',
      },
      {
        value: 'physical-full-maximal-assist',
        label: 'Physical → Full → Maximal Assist',
      },
      {
        value: 'physical-partial-moderate',
        label: 'Physical → Partial → Moderate',
      },
      {
        value: 'physical-partial-minimal',
        label: 'Physical → Partial → Minimal',
      },
      { value: 'verbal-minimal', label: 'Verbal → Minimal' },
      { value: 'verbal-moderate', label: 'Verbal → Moderate' },
      { value: 'verbal-maximum', label: 'Verbal → Maximum' },
      {
        value: 'permanent-visual-prompting',
        label: 'Permanent Visual Prompting',
      },
      { value: 'visual-prompting', label: 'Visual Prompting' },
      { value: 'gestural-prompting', label: 'Gestural Prompting' },
      { value: 'modeling', label: 'Modeling' },
      { value: 'positional-prompting', label: 'Positional Prompting' },
      { value: 'independent', label: 'Independent' },
    ],
  };

  // Check if we have any task analysis sets with steps
  const hasTaskAnalysisWithSteps = taskAnalysisArray.some(
    (taskAnalysisSet) => taskAnalysisSet?.Steps?.length > 0
  );

  // Updated condition: Show if we have original config OR reconstructed config with steps
  if (
    (!hasTaskAnalysis && !hasTaskAnalysisWithSteps) ||
    taskAnalysisArray.length === 0
  ) {
    return null;
  }

  const handleStepMetChange = (taskAnalysisIndex, stepNumber, value) => {
    const currentTaskAnalysis = dataPoint.taskAnalysis || {};
    const stepKey = `taskAnalysis${taskAnalysisIndex}_step${stepNumber}`;
    const updatedTaskAnalysis = {
      ...currentTaskAnalysis,
      [stepKey]: {
        ...currentTaskAnalysis[stepKey],
        met: value === 'yes',
        taskAnalysisIndex,
        stepNumber,
      },
    };
    handleFieldChange(index, 'taskAnalysis', updatedTaskAnalysis);
  };

  const handlePromptingValueChange = (
    taskAnalysisIndex,
    stepNumber,
    option,
    value
  ) => {
    const currentTaskAnalysis = dataPoint.taskAnalysis || {};
    const stepKey = `taskAnalysis${taskAnalysisIndex}_step${stepNumber}`;
    const currentStepData = currentTaskAnalysis[stepKey] || {};
    const currentPromptingValues = currentStepData.promptingValues || {};

    const updatedTaskAnalysis = {
      ...currentTaskAnalysis,
      [stepKey]: {
        ...currentStepData,
        taskAnalysisIndex,
        stepNumber,
        promptingValues: {
          ...currentPromptingValues,
          [option]: value,
        },
      },
    };
    handleFieldChange(index, 'taskAnalysis', updatedTaskAnalysis);
  };

  return (
    <Box sx={{ mt: 3 }}>
      {taskAnalysisArray.map((taskAnalysisSet, taskAnalysisIndex) => {
        const taskAnalysisSteps = taskAnalysisSet?.Steps || [];

        if (taskAnalysisSteps.length === 0) {
          return null;
        }

        return (
          <Box key={taskAnalysisIndex} sx={{ mb: 4 }}>
            {/* Task Analysis Set Header */}
            <Typography
              variant='subtitle1'
              sx={{
                fontWeight: 600,
                mb: 2,
                color: 'var(--clr-mindly-black)',
                borderBottom: '2px solid #e0e0e0',
                pb: 1,
              }}
            >
              Task Analysis #{taskAnalysisIndex + 1}
            </Typography>

            {/* Steps for this Task Analysis Set */}
            {taskAnalysisSteps.map((step, stepIndex) => {
              const stepNumber = step.StepNumber;
              const stepKey = `taskAnalysis${taskAnalysisIndex}_step${stepNumber}`;
              const stepData = dataPoint.taskAnalysis?.[stepKey] || {};
              const isMet = stepData.met || false;
              const promptingValues = stepData.promptingValues || {};
              const stepPromptingValues = step.PromptingValues || {};

              return (
                <Box key={stepIndex} sx={{ mb: 3, ml: 2 }}>
                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant='body1'
                      sx={{
                        fontWeight: 600,
                        minWidth: '60px',
                      }}
                    >
                      Step {stepNumber}:
                    </Typography>
                    <Typography
                      variant='body1'
                      sx={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      {step.Description}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      mb: 2,
                    }}
                  >
                    <FormControl component='fieldset'>
                      <Typography
                        variant='body2'
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Step Met:
                      </Typography>
                      <RadioGroup
                        row
                        value={isMet ? 'yes' : 'no'}
                        onChange={(e) =>
                          handleStepMetChange(
                            taskAnalysisIndex,
                            stepNumber,
                            e.target.value
                          )
                        }
                      >
                        <FormControlLabel
                          value='yes'
                          control={<Radio size='small' />}
                          label='Yes'
                        />
                        <FormControlLabel
                          value='no'
                          control={<Radio size='small' />}
                          label='No'
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {isMet &&
                    step.PromptingType &&
                    Object.keys(stepPromptingValues).length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant='body2'
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          Prompting Values for Step {stepNumber}:
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          {Object.keys(stepPromptingValues).map((option) => {
                            const optionData = promptingOptions[
                              step.PromptingType
                            ]?.find((opt) => opt.value === option);
                            return (
                              <Box
                                key={option}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                }}
                              >
                                <Typography
                                  variant='body2'
                                  sx={{ minWidth: '200px' }}
                                >
                                  {optionData?.label || option}:
                                </Typography>
                                <TextField
                                  type='number'
                                  value={promptingValues[option] || ''}
                                  onChange={(e) =>
                                    handlePromptingValueChange(
                                      taskAnalysisIndex,
                                      stepNumber,
                                      option,
                                      e.target.value
                                    )
                                  }
                                  size='small'
                                  sx={{
                                    width: '100px',
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '8px',
                                      backgroundColor:
                                        'var(--clr-mindly-white)',
                                      border: '1px solid #e0e0e0',
                                      boxShadow: '0px 0px 10px rgba(0,0,0,0.1)',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      border: 'none',
                                    },
                                  }}
                                  inputProps={{
                                    min: 0,
                                    step: 1,
                                    onWheel: preventNumberScroll,
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

export default TaskAnalysisFields;
